import { useEffect, useMemo, useState } from 'react';
import type { DeploymentRecord } from '@/services/deploymentService';
import { resolveDeploymentLogTarget } from '@/services/logsService';
import { deployProgressPoller, type ProgressSnapshot, type ProgressTarget } from '@/utils/deployProgressPoller';
import { isAwaitingRetry, isLive, isStuck } from '@/utils/deployStatus';

export type { ProgressSnapshot };

/** Runs on the page itself are read before the members of a group run. */
export const PROGRESS_PRIORITY = { run: 0, member: 1 } as const;

function targetFor(record: DeploymentRecord, priority: number): ProgressTarget | null {
  // A run that ended has nothing new to say, and a stuck one is dead: neither is worth a slot.
  if (!isLive(record) || isStuck(record)) return null;
  // Waiting for its second attempt: the log on the record is the first attempt's, which is over.
  if (isAwaitingRetry(record)) return null;
  const { group, stream } = resolveDeploymentLogTarget(record);
  const startMs = Date.parse(record.startedAt || '');
  if (!stream || !Number.isFinite(startMs)) return null;
  return { stream, group, startMs, mode: record.mode === 'plan' ? 'plan' : 'deploy', priority };
}

/**
 * Live progress for the runs given, by deployment id. A run with no entry has
 * no task yet. The reads go through one shared poller, which holds the page to
 * six runs at a time: a run past that comes back with `waiting` set.
 */
export function useDeployProgress(
  records: DeploymentRecord[],
  priority: number = PROGRESS_PRIORITY.run
): Record<string, ProgressSnapshot | undefined> {
  const [version, setVersion] = useState(0);

  const targets = useMemo(() => {
    const list: { id: string; target: ProgressTarget }[] = [];
    for (const record of records) {
      const target = targetFor(record, priority);
      if (target) list.push({ id: record.deploymentId, target });
    }
    return list;
  }, [records, priority]);

  // The same runs on the next render must not let go and ask again.
  const signature = targets.map((t) => `${t.id}|${t.target.stream}|${t.target.priority}`).join(',');

  useEffect(() => {
    if (targets.length === 0) return;
    const stopListening = deployProgressPoller.onChange(() => setVersion((v) => v + 1));
    const release = targets.map((t) => deployProgressPoller.subscribe(t.target));
    return () => {
      stopListening();
      for (const letGo of release) letGo();
    };
  }, [signature]);

  return useMemo(() => {
    const byId: Record<string, ProgressSnapshot | undefined> = {};
    for (const { id, target } of targets) byId[id] = deployProgressPoller.get(target.stream);
    return byId;
  }, [signature, version]);
}
